/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import React, { useEffect } from 'react';
import { Upload, notification } from 'antd';
import DocumentUploadSvg from 'Components/common/assets/svgIcons/DocumentUploadSvg';
import styled from 'styled-components';
import {
  secondaryFont, uploadDocumentHoverStyleSnippet, wMBold,
} from 'Utils/utilsStyle';
import {
  ALLOWED_FILE_TYPES, FILE_SIZE_ERROR, FILE_TYPE_ERROR, MAX_FILES_ALLOWED, MAX_FILE_SIZE,
  SMART_REDIRECTION_TEXT,
  WRONG_FILE_UPLOAD_LIMIT,
} from 'Utils/Constants';
import { actions as flurryActions } from 'Slice/HelperSlice';
import { useDispatch, useSelector } from 'react-redux';
import {
  convertToFile, getCurrentTime, getFileSize, getTimezoneInGMT, valueToObject,
} from 'Utils/UtilityFunctions';
import { actions } from 'Slice/DocumentHubSlice';
import { RcFile } from 'antd/es/upload';
import {
  selectBatchSize, selectCurrentFolder, selectFailedDocuments, selectFileTypeFlurry,
  selectFlurryFlag, selectFoldersPopupSelectedFolder, selectNextUploadDocuments,
  selectSuccessDocuments, selectUploadCancelled, selectUploadDocumentStatus, selectUploadDocuments,
} from 'Selector/DocumentHubSelector';
import { selectAiCertificationData } from 'Selector/CertificationSelector';
import { actions as certificationActions } from 'Slice/CertificationSlice';
import { chatStates } from 'Components/layout/headers/NavBarPrimary';
import { getFileType } from 'Components/common/modal/DuplicateFilePopup';
import { DocumentUploaded, FileWithId } from 'Types/DocumentHubTypes';
import UploadDocumentFooter from './UploadDocumentFooter';

const { Dragger } = Upload;
const Wrapper = styled.div`
.ant-upload {
  &.ant-upload-drag-hover {
   ${uploadDocumentHoverStyleSnippet};
  }
  padding: 0px !important;
  border-radius: 4.961px;
  background: linear-gradient(180deg, rgba(217, 217, 217, 0.60) 0%, rgba(232, 232, 232, 0.12) 100%), #F7F7F7;
  &:hover {
    ${uploadDocumentHoverStyleSnippet};
  }
}
.ant-upload-drag-icon {
  display: flex;
  flex-direction: column;
  .content-div {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 60px 20px 60px;
    .document-upload-svg {
      fill: #9DA2A4;
    }
    .textual-div {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      margin-left: 18px;
      .icon-text {
        font-family: ${secondaryFont};
        font-size: 20px;
        line-height: 22px;
        font-weight: ${wMBold};
        letter-spacing: -0.532px;
        color: #9DA2A4;
      }
    }
  }
}
`;

type Props = {
  inSmartDrawer: boolean
};

const UploadComponent = function UploadComponent(props: Props) {
  const {
    inSmartDrawer,
  } = props;
  const dispatch = useDispatch();
  const { saveProfileName: saveProfileNameV2 } = chatStates;
  const startTime = getCurrentTime();
  const timezone = getTimezoneInGMT();
  const batchSize = useSelector(selectBatchSize);
  const uploadDocuments = useSelector(selectUploadDocuments);
  const nextUploadDocuments = useSelector(selectNextUploadDocuments);
  const failedDocuments = useSelector(selectFailedDocuments);
  const successDocuments = useSelector(selectSuccessDocuments);
  const uploadCancelled = useSelector(selectUploadCancelled);
  const aiCertificationData = useSelector(selectAiCertificationData);
  const flurryFlag = useSelector(selectFlurryFlag);
  const flurryArray = useSelector(selectFileTypeFlurry);
  const host = window.location.href;
  const uploadDocumentStatus = useSelector(selectUploadDocumentStatus);
  const selectedFolder = useSelector(selectFoldersPopupSelectedFolder);
  const currentFolder = useSelector(selectCurrentFolder);

  const beforeUpload = (file: RcFile, fileList: RcFile[]) => {
    dispatch(certificationActions.aiCertificationAction({
      type: 'add_file_document',
      source: 'doc_gilroy',
      data: aiCertificationData,
    }));
    if (!uploadCancelled) {
      dispatch(actions.resetBatch());
    }
    if (fileList.length > MAX_FILES_ALLOWED) {
      dispatch(flurryActions.setNotification({
        message: WRONG_FILE_UPLOAD_LIMIT,
        proTip: false,
        rightPlacement: false,
        groupId: 0,
        classnameFlag: false,
      }));
      return false;
    }
    dispatch(actions.setBatchSize(fileList.length));
    return true;
  };

  const redirectFrom = () => {
    if (saveProfileNameV2 && !saveProfileNameV2.includes(SMART_REDIRECTION_TEXT)) {
      return 'gilroy';
    }
    if (inSmartDrawer) {
      return 'smartAccountPlan';
    }
    return 'docHub';
  };

  const handleCustomRequest = (options: any) => {
    const { file } = options;
    if (file) {
      notification.destroy();
      const { size, type } = file;
      if (!type?.length || !ALLOWED_FILE_TYPES.includes(type as string)) {
        dispatch(actions.setAllUploadDocument({ file: file as FileWithId, status: 'pending' }));
        dispatch(actions
          .setFailDocuments({
            uid: file.uid,
            name: file.name,
            size: getFileSize(file.size as number),
            failReason: FILE_TYPE_ERROR,
          }));
      } else if (size && size >= MAX_FILE_SIZE) {
        dispatch(actions.setAllUploadDocument({ file: file as FileWithId, status: 'pending' }));
        dispatch(actions.setFailDocuments({
          uid: file.uid,
          name: file.name,
          size: getFileSize(file.size as number),
          failReason: FILE_SIZE_ERROR,
        }));
      } else {
        dispatch(actions.pushFileTypeFlurry(getFileType(type as string)));
        notification.destroy();
        dispatch(actions.setAllUploadDocument({ file: file as FileWithId, status: 'pending' }));
        if (uploadCancelled) {
          dispatch(actions.setNextUploadDocument(file as FileWithId));
        } else {
          dispatch(actions.setUploadDocument(file as FileWithId));
        }
      }
    }
  };

  const handleProgress = (progress: any) => {
    dispatch(actions.setDocumentUploadCount(progress));
  };

  useEffect(() => {
    if (!inSmartDrawer) {
      if (uploadDocumentStatus && (selectedFolder?.id || currentFolder?.id)) {
        const file = uploadDocuments[0];
        if (file) {
          if (file.uid === 'cancelled') {
            dispatch(actions.documentUploaded({} as DocumentUploaded));
            const uploadFile = file;
            dispatch(actions.setFailDocuments({
              uid: file?.uid as string,
              name: uploadFile.name,
              size: getFileSize(uploadFile?.size),
              failReason: 'Upload cancelled',
              type: '',
              isLink: false,
            }));
            dispatch(actions.removeCancelledUploadDocument(file.uid));
          } else {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onloadend = () => {
              if (flurryFlag) {
                dispatch(flurryActions.callFlurryEvent(
                  valueToObject('documentHub_upload_fileSelection_confirmed', startTime, {
                    file_type: flurryArray.join(','),
                    redirection_from: redirectFrom(),
                    host,
                  }),
                ));
              }
              const convertedFile = convertToFile(reader.result as string, file.name);
              const fileWithUid = Object.assign(convertedFile, {
                uid: file.uid,
              });
              dispatch(actions.uploadDocument({
                folderId: currentFolder?.id || selectedFolder?.id || 0,
                file: fileWithUid,
                timezone,
                handleProgress,
              }));
              dispatch(actions.setUploadDocumentStatus({ filenameAllowed: false }));
            };
          }
        }
      }
    }
  }, [uploadDocumentStatus, uploadDocuments.length, failedDocuments.length]);

  useEffect(() => {
    if (!inSmartDrawer) {
      if (uploadDocuments.length && (successDocuments.length
        + uploadDocuments.length + failedDocuments.length === batchSize)
        && (selectedFolder?.id || currentFolder?.id)) {
        const file = uploadDocuments[0];
        dispatch(actions.validateFileName({ name: file.name, timezone, folderId: selectedFolder?.id || currentFolder?.id || 0 }));
      }
    }
  }, [uploadDocuments, failedDocuments]);

  useEffect(() => {
    if (!uploadCancelled && nextUploadDocuments.length) {
      dispatch(actions.resetBatch());
      dispatch(actions.setBatchSize(nextUploadDocuments.length));
      nextUploadDocuments.forEach((doc) => dispatch(actions.setUploadDocument(doc)));
      dispatch(actions.resetNextUploadDocuments());
    }
  }, [uploadCancelled]);

  const handleClick = () => {
    dispatch(flurryActions.callFlurryEvent(valueToObject('documentHub_upload_fileSelection_intent', 0, {
      redirection_from: redirectFrom(),
      host,
    })));
  };

  return (
    <Wrapper className="upload-document-wrapper" onClick={handleClick}>
      <Dragger
        maxCount={10}
        multiple
        customRequest={handleCustomRequest}
        beforeUpload={beforeUpload}
        showUploadList={false}
        accept={ALLOWED_FILE_TYPES.join(',')}
      >
        <div className="ant-upload-drag-icon">
          <div className="content-div">
            <DocumentUploadSvg />
            <div className="textual-div">
              <span className="icon-text">Click or Drop files</span>
              <span className="icon-text">here to upload</span>
            </div>
          </div>
          <UploadDocumentFooter maxLimit />
        </div>
      </Dragger>
    </Wrapper>
  );
};

export default UploadComponent;
